import { GoogleGenerativeAI, Content, type Tool, SchemaType } from "@google/generative-ai";
import { countryName, searchCourses as searchLiveCourses, type CourseCountry } from './coursesApi';

export interface ChatbotConfig {
    geminiApiKey: string;
}

export interface ChatMessage {
    role: 'user' | 'model';
    text: string;
}

export interface ChatbotResponse {
    success: boolean;
    text?: string;
    error?: string;
    navigation?: string;
}

const DEFAULT_SYSTEM_PROMPT = `You are an expert education consultant for "Global Education Consultants" (GEC).
Your primary goal is to help prospective students. Engage in a friendly, conversational, and professional manner.

You have two tools available:

**1. Course Search Tool: \`searchCourses(country, course_title)\`**
Use this to search our live database for university courses.
- **Valid Countries for Search:** The live course database currently covers 'Australia' and 'New Zealand'.
- **Handling Other Countries:** If the user asks about another country, explain that the live course finder currently covers Australia and New Zealand and offer to help them contact a counsellor.
- **Context is King:** Combine conversation context. If user says "Australia" then "data science", you MUST call \`searchCourses({country: "Australia", course_title: "data science"})\`.
- **Be Flexible:** If a specific search fails, try a broader one (e.g., "Master of Cybersecurity" -> "Cybersecurity").

**2. Navigation Tool: \`navigateToPage(page)\`**
Use this when the user explicitly asks to go to a specific page on the website.
- You must use one of the predefined page paths.
- **Valid Pages:** '/about', '/courses', '/colleges', '/events', '/consultation', '/delegate-meetings', '/services', '/contact', '/blog', '/terms'.

Do not use tools for simple greetings. Only use them when the user's intent matches the tool's purpose.`;

const tools: Tool[] = [{
    functionDeclarations: [
        {
            name: "searchCourses",
            description: "Searches for available university courses in the database based on country and course title/university name.",
            parameters: {
                type: SchemaType.OBJECT,
                properties: {
                    country: { type: SchemaType.STRING, description: "The country to search for courses in. Valid options are 'Australia' and 'New Zealand'." },
                    course_title: { type: SchemaType.STRING, description: "The course title or university name to search for (e.g., 'Engineering', 'Monash University')." }
                },
                required: ['country']
            }
        },
        {
            name: "navigateToPage",
            description: "Navigates the user to an internal page on the website.",
            parameters: {
                type: SchemaType.OBJECT,
                properties: {
                    page: { 
                        type: SchemaType.STRING,
                        format: "enum",
                        description: "The path of the page to navigate to.",
                        enum: ['/about', '/courses', '/colleges', '/events', '/consultation', '/delegate-meetings', '/services', '/contact', '/blog', '/terms']
                    }
                },
                required: ['page']
            }
        }
    ]
}];

const searchCoursesInDb = async ({ country, course_title }: { country?: string; course_title?: string; }) => {
    try {
        const normalizedCountry = country?.trim().toLowerCase();
        const countryCode: CourseCountry | undefined = normalizedCountry === 'australia' || normalizedCountry === 'au'
            ? 'AU'
            : normalizedCountry === 'new zealand' || normalizedCountry === 'nz'
                ? 'NZ'
                : undefined;

        if (!countryCode) return { courses: [] };

        const result = await searchLiveCourses({
            country: countryCode,
            search: course_title || '',
            pageSize: 8,
        });

        const courses = result.courses.map((course) => ({
            university: course.provider_name,
            title: course.name,
            level: course.level_of_study || 'To be confirmed',
            duration: course.duration || 'To be confirmed',
            country: countryName(course.country),
        }));

        return { courses };
    } catch (e) {
        console.error("Exception during course search:", e);
        return { courses: [] };
    }
};

export function createChatbotService(config: ChatbotConfig) {
    if (!config.geminiApiKey) {
        throw new Error("ChatbotService: Gemini API Key must be provided.");
    }

    const genAI = new GoogleGenerativeAI(config.geminiApiKey);
    const model = genAI.getGenerativeModel({ model: "models/gemini-flash-latest", tools, systemInstruction: DEFAULT_SYSTEM_PROMPT });

    return {
        async getResponse(history: ChatMessage[], newMessage: string): Promise<ChatbotResponse> {
            try {
                const geminiHistory: Content[] = history.map(msg => ({ role: msg.role, parts: [{ text: msg.text }] }));
                const chat = model.startChat({ history: geminiHistory });
                let result = await chat.sendMessage(newMessage);
                let navigationPath: string | undefined = undefined;

                while (true) {
                    const functionCalls = result.response.functionCalls();
                    if (!functionCalls || functionCalls.length === 0) {
                        break;
                    }

                    const call = functionCalls[0];
                    console.log("Function call detected:", call);

                    if (call.name === 'searchCourses') {
                        const dbResult = await searchCoursesInDb(call.args as any);
                        result = await chat.sendMessage([{ functionResponse: { name: "searchCourses", response: dbResult } }]);
                    } else if (call.name === 'navigateToPage') {
                        navigationPath = (call.args as any).page;
                        result = await chat.sendMessage([{ functionResponse: { name: "navigateToPage", response: { status: "success" } } }]);
                    } else {
                        break;
                    }
                }

                const text = result.response.text();
                return { success: true, text, navigation: navigationPath };

            } catch (error: any) {
                console.error("Error in chatbotService.getResponse:", error);
                return { success: false, error: "Failed to get response from AI." };
            }
        }
    };
}
